import React from "react";
import PnoeDetailSection, { PnoeField } from "./PnoeDetailSection";
import {
  PnoeMeasurementType,
  PnoeProtocol,
} from "../../entities/pnoeMeasurement";
import { PnoeParameterOptions } from "./usePnoeParameters";
import { titleCaseOption } from "./pnoeFieldFormatting";

// There are no predefined protocols any more — just the parameters chosen before
// the test starts. Each test type has its own set, and the other type's fields
// are hidden entirely rather than shown empty.
export const AMR_PROTOCOL_FIELDS = [
  { key: "amr_equipment_type", label: "Equipment" },
  { key: "amr_protocol_level", label: "Protocol level" },
] as const;

export const RMR_PROTOCOL_FIELDS = [
  { key: "rmr_last_meal", label: "Last meal" },
  { key: "rmr_last_workout", label: "Last workout" },
  { key: "rmr_last_coffee", label: "Last coffee" },
  { key: "rmr_posture", label: "Posture" },
] as const;

/**
 * The design's column stacks per test type (node 16827:67296) — RMR is 2/1/1,
 * with Posture wrapping under Last meal.
 */
const COLUMNS: Record<PnoeMeasurementType, string[][]> = {
  rmr: [["rmr_last_meal", "rmr_posture"], ["rmr_last_workout"], ["rmr_last_coffee"]],
  amr: [["amr_equipment_type"], ["amr_protocol_level"], []],
};

interface PnoeProtocolSectionProps {
  measurementType: PnoeMeasurementType;
  protocol: PnoeProtocol;
  options: PnoeParameterOptions;
  /**
   * Opens the edit panel. Omitted hides the pencil — which is every AMR test,
   * since `updatePnoeProtocol` takes RMR parameters only.
   */
  onEdit?: () => void;
  editDisabled?: boolean;
  editDisabledReason?: string;
}

const PnoeProtocolSection: React.FC<PnoeProtocolSectionProps> = ({
  measurementType,
  protocol,
  options,
  onEdit,
  editDisabled,
  editDisabledReason,
}) => {
  const labelFor = (fieldKey: string) => {
    const raw = (protocol as any)[fieldKey];
    if (!raw) return "";
    const match = options[fieldKey]?.find((option) => option.value === raw);
    return match?.label ?? titleCaseOption(String(raw));
  };

  const fields: readonly { key: string; label: string }[] =
    measurementType === "rmr" ? RMR_PROTOCOL_FIELDS : AMR_PROTOCOL_FIELDS;
  const labelOf = (key: string) =>
    fields.find((field) => field.key === key)?.label ?? "";

  return (
    <PnoeDetailSection
      title="Protocol"
      variant="inline"
      onEdit={onEdit}
      editDisabled={editDisabled}
      editDisabledReason={editDisabledReason}
      columns={COLUMNS[measurementType].map((keys) =>
        keys.map((key) => (
          <PnoeField key={key} label={labelOf(key)} value={labelFor(key)} />
        ))
      )}
    />
  );
};

export default PnoeProtocolSection;
